/**
 * Backblaze B2 configuration for motorsport photos.
 *
 * Public download URL shape:
 *   https://{endpoint}/file/{bucketName}/{folder}/{filename}
 * Example:
 *   https://f005.backblazeb2.com/file/pradap-motorsport/asian-le-mans-2025/01.jpg
 *
 * Configure via Vite env (local `.env` or Railway service variables):
 *   VITE_B2_ENABLED=true
 *   VITE_B2_BUCKET=pradap-motorsport
 *   VITE_B2_ENDPOINT=f005.backblazeb2.com
 *   VITE_B2_FRIENDLY_BASE_URL=   (optional CDN / friendly URL)
 *   VITE_B2_ROOT_FOLDER=        (optional prefix inside the bucket)
 *
 * Vite bakes VITE_* at build time — set them before `npm run build` / Railway deploy.
 */

function env(key: keyof ImportMetaEnv, fallback = ''): string {
  const value = import.meta.env[key];
  return typeof value === 'string' ? value.trim() : fallback;
}

function envFlag(key: keyof ImportMetaEnv): boolean {
  const value = env(key).toLowerCase();
  return value === 'true' || value === '1' || value === 'yes';
}

const bucketName = env('VITE_B2_BUCKET', 'pradap-motorsport');
const endpoint = env('VITE_B2_ENDPOINT', '').replace(/^https?:\/\//, '').replace(/\/$/, '');
const friendlyBaseUrl = env('VITE_B2_FRIENDLY_BASE_URL').replace(/\/$/, '');
const rootFolder = env('VITE_B2_ROOT_FOLDER').replace(/^\/|\/$/g, '');

/** Live when explicitly enabled, or when a friendly CDN URL is set */
const enabled = envFlag('VITE_B2_ENABLED') || Boolean(friendlyBaseUrl);

export const backblazeConfig = {
  enabled,
  bucketName,
  endpoint,
  friendlyBaseUrl,
  rootFolder
};

/** Build a public B2 (or friendly CDN) URL for a gallery file */
export function b2Url(galleryFolder: string, filename: string): string {
  const { enabled: isLive, bucketName: bucket, endpoint: host, friendlyBaseUrl: friendly, rootFolder: root } =
    backblazeConfig;

  if (friendly) {
    const folder = [root, galleryFolder].filter(Boolean).join('/');
    return `${friendly}/${folder}/${filename}`;
  }

  if (!isLive || !host || !bucket) {
    return '';
  }

  const folder = [root, galleryFolder].filter(Boolean).join('/');
  return `https://${host}/file/${bucket}/${folder}/${filename}`;
}

/** True when gallery should load remote images instead of SVG placeholders */
export function isB2Live(): boolean {
  return Boolean(b2Url('probe', 'probe.jpg'));
}
